/**
 * Opportunity Visibility Resolver (P3)
 *
 * SINGLE SOURCE OF TRUTH for "which jobs is contractor X allowed to see?"
 *
 * Locked spec (P3):
 *   1. Modes: offer_only (default), limited_exposure, none.
 *   2. Tier → mode hardcoded:
 *        Elite     → limited_exposure
 *        Reliable  → limited_exposure
 *        Watch     → offer_only
 *        Risk      → none
 *        null/insufficient (sampleSize < 3) → offer_only
 *   3. Per-contractor override beats platform default. Platform default = offer_only.
 *   4. limited_exposure caps visible jobs at MAX_LIMITED_EXPOSURE_JOBS.
 *   5. Even in limited_exposure, jobs MUST pass:
 *        - eligibility (resolveContractorEligibility, ALL blocking factors)
 *        - geo (reuses eligibility coverage factor — no second geo check)
 *        - dispatch-eligible stage
 *        - not already assigned (assignedSubcontractorId)
 *        - no peer accepted offer
 *        - no live pending/viewed offer to ANOTHER contractor
 *   6. Offers ALREADY pending to THIS contractor are excluded (they live in
 *      the offer panel, not the opportunities tab).
 *   7. Pricing: returns payoutAmount snapshot only. NEVER customer revenue or
 *      internal pricing. Enforced by p3Activation guardrail tests.
 *
 * Pure function. Caller owns persistence and routing.
 */

import type { Contractor, Job, JobStage } from "@/data/types";
import type { ContractorPayProfile } from "@/data/contractorPayProfile";
import type { MarketplaceOffer } from "./marketplaceOffer";
import type { OfferMetrics } from "./offerMetrics";
import { MIN_SAMPLE_SIZE } from "./offerMetrics";
import { resolveContractorEligibility } from "./marketplaceEligibility";
import {
  getContractorReliability,
  type ReliabilityTier,
} from "@/data/contractorReliability";
import type { ContractorScorecard } from "@/hooks/useContractorScorecards";
import { isMigrationApproved } from "@/data/import/migrationVisibilityGuard";

export type VisibilityMode = "offer_only" | "limited_exposure" | "none";

export const PLATFORM_DEFAULT_VISIBILITY_MODE: VisibilityMode = "offer_only";
export const MAX_LIMITED_EXPOSURE_JOBS = 5;

/** Stages where dispatchers can offer a contractor — i.e. job is on the market. */
export const DISPATCH_ELIGIBLE_STAGES: ReadonlySet<JobStage> = new Set<JobStage>([
  "Ready for Dispatch",
  "Available for Contractor Assignment",
]);

export interface VisibleOpportunity {
  jobId: string;
  city: string;
  zip: string;
  projectType: string;
  scheduledDate?: string;
  /** Contractor pay snapshot ONLY — never customer revenue. */
  payoutAmount: number;
}

export interface VisibilityResolution {
  mode: VisibilityMode;
  tier: ReliabilityTier | null;
  /** True when the resolver returned mode strictly because metrics were insufficient. */
  insufficientData: boolean;
  /** True when a per-contractor override beat the tier-derived mode. */
  overrideApplied: boolean;
  opportunities: VisibleOpportunity[];
}

export interface VisibilityContext {
  payProfiles: ContractorPayProfile[];
  /** Per-contractor override (rare). When undefined, falls through to tier→mode. */
  contractorVisibilityOverride?: VisibilityMode | null;
  /** Platform default. Defaults to PLATFORM_DEFAULT_VISIBILITY_MODE. */
  platformDefaultMode?: VisibilityMode;
  /** Optional cap override for testing. */
  maxLimitedExposureJobs?: number;
}

/* ── Tier → mode mapping (hardcoded per locked P3 spec) ────────────── */

export function tierToVisibilityMode(
  tier: ReliabilityTier | null,
): VisibilityMode {
  if (tier === null) return "offer_only";
  switch (tier) {
    case "Elite":
      return "limited_exposure";
    case "Reliable":
      return "limited_exposure";
    case "Watch":
      return "offer_only";
    case "Risk":
      return "none";
  }
}

/**
 * Determine effective tier from canonical metrics. Returns null when sample
 * size is insufficient (mirrors offerMetrics rule).
 *
 * We derive tier from the scorecard wrapper (which already incorporates
 * canonical offer metrics — see useContractorScorecards.ts). We layer
 * the sample-size gate on top: even if the scorecard would emit a tier,
 * we honor null-tier when canonical offer sample size < MIN_SAMPLE_SIZE.
 */
export function resolveEffectiveTier(
  metrics: OfferMetrics | null,
  scorecard: ContractorScorecard | null,
): { tier: ReliabilityTier | null; insufficient: boolean } {
  // Sample-size gate first.
  if (!metrics || metrics.sampleSize < MIN_SAMPLE_SIZE) {
    return { tier: null, insufficient: true };
  }
  if (!scorecard) return { tier: null, insufficient: true };
  const reliability = getContractorReliability(scorecard);
  return { tier: reliability.tier, insufficient: false };
}

/* ── Stale visibility filter ───────────────────────────────────────── */

/**
 * Returns the set of jobIds that should NOT be visible to ANY contractor
 * other than one they're explicitly assigned/offered to:
 *   - Jobs already assigned (assignedSubcontractorId set)
 *   - Jobs with an accepted offer
 *   - Jobs with a pending or viewed offer to ANY contractor
 */
function jobIdsLocked(jobs: Job[], offers: MarketplaceOffer[]): Set<string> {
  const locked = new Set<string>();
  for (const j of jobs) {
    if (j.assignedSubcontractorId) locked.add(j.id);
  }
  for (const o of offers) {
    if (o.status === "accepted" || o.status === "pending") {
      locked.add(o.jobId);
    }
  }
  return locked;
}

/* ── Public resolver ───────────────────────────────────────────────── */

export function getVisibleOpportunities(
  contractor: Contractor,
  jobs: Job[],
  offers: MarketplaceOffer[],
  metrics: OfferMetrics | null,
  scorecard: ContractorScorecard | null,
  context: VisibilityContext,
): VisibilityResolution {
  const platformDefault =
    context.platformDefaultMode ?? PLATFORM_DEFAULT_VISIBILITY_MODE;
  const cap = context.maxLimitedExposureJobs ?? MAX_LIMITED_EXPOSURE_JOBS;

  // 1. Tier resolution (with insufficient-data gate).
  const { tier, insufficient } = resolveEffectiveTier(metrics, scorecard);
  const tierDerivedMode = tierToVisibilityMode(tier);

  // 2. Apply override / platform default precedence:
  //    override (when defined and non-null) > tier-derived mode > platform default.
  let mode: VisibilityMode;
  let overrideApplied = false;
  if (
    context.contractorVisibilityOverride !== undefined &&
    context.contractorVisibilityOverride !== null
  ) {
    mode = context.contractorVisibilityOverride;
    overrideApplied = true;
  } else if (tier !== null) {
    mode = tierDerivedMode;
  } else {
    // Insufficient data: fall back to platform default (which itself defaults
    // to offer_only — conservative).
    mode = platformDefault;
  }

  // Modes that surface no opportunities.
  if (mode === "offer_only" || mode === "none") {
    return {
      mode,
      tier,
      insufficientData: insufficient,
      overrideApplied,
      opportunities: [],
    };
  }

  // 3. limited_exposure: filter the job set.
  const locked = jobIdsLocked(jobs, offers);

  // Exclude jobs the contractor already has an open offer on (they belong
  // in the offer queue, not the opportunities tab).
  const myOpenOfferJobIds = new Set(
    offers
      .filter(
        (o) =>
          o.contractorId === contractor.id &&
          (o.status === "pending" || o.status === "accepted"),
      )
      .map((o) => o.jobId),
  );

  const candidates = jobs.filter((j) => {
    // Hard gate: imported jobs awaiting migration review NEVER surface as
    // opportunities. Native Koredor jobs (no sourceMeta) pass through.
    // Anchor: mem://features/jobber-import-bridge
    if (!isMigrationApproved(j)) return false;
    if (!DISPATCH_ELIGIBLE_STAGES.has(j.currentStage)) return false;
    if (locked.has(j.id) && !myOpenOfferJobIds.has(j.id)) return false;
    if (myOpenOfferJobIds.has(j.id)) return false;

    // Eligibility (covers profile, pay profile, compliance, GEO/coverage,
    // opt-in, override). Single source of truth — no second geo check.
    const eligibility = resolveContractorEligibility(contractor, {
      payProfiles: context.payProfiles,
      job: j,
    });
    return eligibility.eligible;
  });

  // Stable ordering: scheduled date asc, then jobId.
  candidates.sort((a, b) => {
    const ad = a.scheduledInstallDate ?? "";
    const bd = b.scheduledInstallDate ?? "";
    if (ad !== bd) return ad < bd ? -1 : 1;
    return a.id < b.id ? -1 : 1;
  });

  const opportunities: VisibleOpportunity[] = candidates
    .slice(0, cap)
    .map((j) => ({
      jobId: j.id,
      city: j.address?.city ?? "",
      zip: j.address?.zip ?? "",
      projectType: j.projectType,
      scheduledDate: j.scheduledInstallDate || undefined,
      payoutAmount: j.payoutAmount,
    }));

  return {
    mode,
    tier,
    insufficientData: insufficient,
    overrideApplied,
    opportunities,
  };
}
